import { useCallback, useEffect, useRef, useState } from "react";
import "./AppStatusBanner.css";

const POLL_MS = 60 * 1000;
const isProd = process.env.NODE_ENV === "production";

async function fetchBuildId() {
  const res = await fetch(`/version.json?t=${Date.now()}`, {
    cache: "no-store",
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data?.buildId || null;
}

export default function AppStatusBanner() {
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== "undefined" ? !navigator.onLine : false,
  );
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const initialBuildIdRef = useRef(null);

  const checkForUpdate = useCallback(async () => {
    if (!isProd || !navigator.onLine) return;
    try {
      const buildId = await fetchBuildId();
      if (!buildId) return;
      if (initialBuildIdRef.current == null) {
        initialBuildIdRef.current = buildId;
        return;
      }
      if (buildId !== initialBuildIdRef.current) {
        setUpdateAvailable(true);
      }
    } catch {
      // Network failures are handled by the offline banner.
    }
  }, []);

  useEffect(() => {
    const onOffline = () => setIsOffline(true);
    const onOnline = () => {
      setIsOffline(false);
      checkForUpdate();
    };
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, [checkForUpdate]);

  useEffect(() => {
    if (!isProd) return undefined;
    checkForUpdate();
    const interval = setInterval(checkForUpdate, POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [checkForUpdate]);

  const showOffline = isOffline;
  const showUpdate = !isOffline && updateAvailable && !dismissed;

  if (!showOffline && !showUpdate) return null;

  return (
    <div className="sx-app-status" role="status" aria-live="polite">
      {showOffline ? (
        <div className="sx-app-status-banner sx-app-status-banner--offline">
          <span className="sx-app-status-dot" aria-hidden />
          <p className="sx-app-status-copy">No internet connection</p>
        </div>
      ) : (
        <div className="sx-app-status-banner sx-app-status-banner--update">
          <p className="sx-app-status-copy">A new update is available</p>
          <div className="sx-app-status-actions">
            <button
              type="button"
              className="sx-app-status-later"
              onClick={() => setDismissed(true)}
            >
              Later
            </button>
            <button
              type="button"
              className="sx-app-status-reload"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
